/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { lazy, Suspense, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import HeaderAndSlider from "../Components/HeaderAndSlider";
import { Vocabulary } from "./Vocabulary";
import Dashboard from "../Components/Dashboard/Dashboard";
import LoginPage from "../Components/LoginPage";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import withRole from "./withRole";
import type { Router } from "@remix-run/router";
import { componentNames } from "./Constants";
import { localUrlEnum } from "./urlEnum";
import VehicleOptionSlider from "../Components/VehicleDasboard/VehicleOptionSlider";

const Reports = lazy(() => import("../Components/Reports/Reports"));
const AdministrativeCostsDashboard = lazy(
  () => import("../Components/AdministrativeCosts/AdministrativeCostsDashboard")
);
const Logs = lazy(() => import("../Components/Logs/Logs"));
const UsersDashboard = lazy(() => import("../Components/Users/UsersDashboard"));
const MainSettings = lazy(() => import("../Components/Settings/MainSettings"));
const MainDashboard = lazy(() => import("../Components/MainDashboard"));
const VehicleDetails = lazy(() => import("../Components/VehicleDetails"));
const VehicleService = lazy(() => import("../Components/VehicleService"));
const VehicleDetailing = lazy(() => import("../Components/VehicleDetailing"));
const VehiclePainting = lazy(() => import("../Components/VehiclePainting"));
const VehicleAdditionalCost = lazy(
  () => import("../Components/VehicleAdditionalCost")
);
const VehiclePhotos = lazy(() => import("../Components/VehiclePhotos"));
const VehicleDocuments = lazy(() => import("../Components/VehicleDocuments"));
const VehicleGuarantees = lazy(() => import("../Components/VehicleGuarantees"));
const Clients = lazy(() => import("../Components/Clients/Clients"));
const NotFoundPage = lazy(() => import("./NotFoundPage"));
const DashboardFinancial = lazy(
  () => import("../Components/FinancialDepartment/DashboardFinancial")
);
const MainTemplates = lazy(() => import("../Components/Templates/MainTemplates"));
const DashboardClientMessages = lazy(
  () => import("../Components/MessageLists/DashboardClientMessages")
);
const LeadDetail = lazy(() => import("../Components/LeadManagement/LeadDetail"));
const DashboardSalesOnDemand = lazy(
  () => import("../Components/Sales/DashboardSalesOnDemand")
);
const DashboardFinancingMessages = lazy(
  () => import("../Components/MessageLists/DashboardFinancingMessages")
);
const DashboardVINRequests = lazy(
  () => import("../Components/MessageLists/DashboardVINRequests")
);
const AnafTable = lazy(() => import("../Components/ANAF/AnafTable"));
const UserSalaries = lazy(() => import("../Components/Salary/UserSalaries"));
const SecondVehiclesDashboard = lazy(
  () => import("../Components/VehicleDasboard/SecondVehiclesDashboard")
);
const DocumentsTable = lazy(() => import("../Components/Documents/DocumentsTable"));
const SalaryDashboard = lazy(() => import("../Components/Dashboard/SalaryDashboard"));
const XAutoDashboard = lazy(() => import("../Components/XAuto/XAutoDashboard"));
const Applications = lazy(() => import("../Components/Settings/Applications"));
const TasksBacklog = lazy(() => import("../Components/Tasks/TasksBacklog"));
const CalendarDashboard = lazy(() => import("../Components/Calendar/CalendarDashboard"));

type LocalRouterProps = {
  routes: Array<string> | undefined;
};

function addVehicleOptionsMenu(component: any, title: string) {
  return <VehicleOptionSlider title={title} component={component} />;
}

function addHeaderAndMenu(component: any, title: string) {
  return <HeaderAndSlider key={title} title={title} component={component} />;
}

function RouteLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress color="primary" aria-label={Vocabulary.loading} />
    </div>
  );
}

const authRoutes = [
  {
    path: "/login",
    element: <LoginPage />,
  },
];

const dashboardRoutes = [
  {
    path: "/home",
    element: addHeaderAndMenu(<Dashboard />, Vocabulary.dashboard),
  },
  {
    path: "/",
    element: addHeaderAndMenu(
      <Dashboard name={componentNames.Dashboard} />,
      Vocabulary.dashboard
    ),
  },
];

const vehicleRoutes = [
  {
    path: "/vehiclesDashboard",
    element: addHeaderAndMenu(
      <MainDashboard name="MainDashboard" />,
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDashboard/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(
        <SecondVehiclesDashboard name={componentNames.VehiclesDashboard} />,
        Vocabulary.vehicles
      ),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDetails/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(
        <VehicleDetails name={componentNames.VehicleMainInfo} />,
        Vocabulary.vehicles
      ),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleGuarantees/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleGuarantees />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleService/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleService />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDetailing/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleDetailing />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehiclePainting/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehiclePainting />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleAdditionalCost/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleAdditionalCost />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehiclePhotos/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehiclePhotos />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDocuments/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleDocuments />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: localUrlEnum.xautoVehiclesActive,
    element: addHeaderAndMenu(
      <XAutoDashboard />,
      Vocabulary.xautoVehiclesActive
    ),
  },
  {
    path: localUrlEnum.xautoVehiclesDeactivated,
    element: addHeaderAndMenu(
      <XAutoDashboard />,
      Vocabulary.xautoVehiclesDeactivated
    ),
  },
];

const salesAndFinancialRoutes = [
  {
    path: "/financialDepartment",
    element: addHeaderAndMenu(
      <DashboardFinancial />,
      Vocabulary.dashboardFinancial
    ),
  },
  {
    path: "/onDemand",
    element: addHeaderAndMenu(
      <DashboardSalesOnDemand
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.onDemand
    ),
  },
  {
    path: "/administrativeCosts",
    element: addHeaderAndMenu(
      <AdministrativeCostsDashboard />,
      Vocabulary.costsName
    ),
  },
];

const clientAndLeadRoutes = [
  {
    path: "/clients",
    element: addHeaderAndMenu(<Clients />, Vocabulary.clientsName),
  },
  {
    path: localUrlEnum.clientMessages,
    element: addHeaderAndMenu(
      <DashboardClientMessages
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.clientMessages
    ),
  },
  {
    path: `${localUrlEnum.clientMessages}/:id`,
    element: addHeaderAndMenu(<LeadDetail />, Vocabulary.clientMessages),
  },
  {
    path: localUrlEnum.financingRequests,
    element: addHeaderAndMenu(
      <DashboardFinancingMessages
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.financingRequests
    ),
  },
  {
    path: localUrlEnum.vinRequests,
    element: addHeaderAndMenu(
      <DashboardVINRequests name={componentNames.DashboardClientMessages} />,
      Vocabulary.vinRequests
    ),
  },
];

const adminRoutes = [
  {
    path: "/applications",
    element: addHeaderAndMenu(<Applications />, Vocabulary.applications),
  },
  {
    path: "/usersDashboard",
    element: addHeaderAndMenu(<UsersDashboard />, Vocabulary.usersName),
  },
  {
    path: "/settings",
    element: addHeaderAndMenu(<MainSettings />, Vocabulary.settings),
  },
  {
    path: "/templates",
    element: addHeaderAndMenu(<MainTemplates />, Vocabulary.templatesName),
  },
  {
    path: "/logs",
    element: addHeaderAndMenu(<Logs />, Vocabulary.logsName),
  },
];

const salaryRoutes = [
  {
    path: "/salaries",
    element: addHeaderAndMenu(<UserSalaries />, Vocabulary.salaries),
  },
  {
    path: "/salaries/user/:id?/:month?/:year?",
    element: addHeaderAndMenu(
      <SalaryDashboard name={componentNames.SalaryComponent} />,
      Vocabulary.dashboard
    ),
  },
];

const operationsRoutes = [
  {
    path: "/anaf/notifications",
    element: addHeaderAndMenu(<AnafTable />, Vocabulary.anafTable),
  },
  {
    path: "/documents",
    element: addHeaderAndMenu(<DocumentsTable />, Vocabulary.documentsTable),
  },
  {
    path: "/tasks-backlog",
    element: addHeaderAndMenu(
      <TasksBacklog name={componentNames.TasksBacklog} />,
      Vocabulary.tasksName
    ),
  },
  {
    path: "/calendar",
    element: addHeaderAndMenu(<CalendarDashboard />, Vocabulary.calendarName),
  },
  {
    path: "/reports",
    element: addHeaderAndMenu(<Reports />, Vocabulary.reportsName),
  },
];

const notFoundRoute = {
  path: "*",
  element: addHeaderAndMenu(<NotFoundPage />, Vocabulary.paginaNegasita),
};

const routerPaths = [
  ...authRoutes,
  ...dashboardRoutes,
  ...vehicleRoutes,
  ...salesAndFinancialRoutes,
  ...clientAndLeadRoutes,
  ...adminRoutes,
  ...salaryRoutes,
  ...operationsRoutes,
  notFoundRoute,
];

function LocalRouter(props: LocalRouterProps) {
  const [router, setRouter] = useState<Router>();

  useEffect(() => {
    if (!props.routes) return;

    const newRouterPaths = routerPaths.filter((crtRoute) => {
      return props.routes && props.routes.indexOf(crtRoute.path) >= 0;
    });
    setRouter(createBrowserRouter(newRouterPaths));
  }, [props]);

  return (
    router && (
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    )
  );
}

export default withRole(LocalRouter);
